// Debounces per-tile visibility results so refinement does not flicker. A tile counts as occluded
// only after `occludedChecks` consecutive checks found none of its pixels; one check that finds a
// pixel makes it visible again at once, because a tile wrongly kept coarse is worse than an extra
// download. Tiles that were never checked are visible.

export class HsOcclusionHysteresis<K> {
  private readonly hiddenStreaks: Map<K, number> = new Map<K, number>();

  constructor(public occludedChecks: number = 3) {}

  /** Records one visibility check of the tiles in `checked`; `visible` holds those with pixels. */
  public observe(checked: Iterable<K>, visible: ReadonlySet<K>): void {
    for (const key of checked) {
      if (visible.has(key)) {
        this.hiddenStreaks.delete(key);
      } else {
        this.hiddenStreaks.set(key, (this.hiddenStreaks.get(key) ?? 0) + 1);
      }
    }
  }

  public isOccluded(key: K): boolean {
    return (this.hiddenStreaks.get(key) ?? 0) >= this.occludedChecks;
  }

  /** Hidden at least once but not yet often enough to count as occluded: another check is due. */
  public isPending(key: K): boolean {
    const streak: number = this.hiddenStreaks.get(key) ?? 0;
    return streak > 0 && streak < this.occludedChecks;
  }

  public get occludedCount(): number {
    let count: number = 0;
    this.hiddenStreaks.forEach((streak: number) => {
      count += streak >= this.occludedChecks ? 1 : 0;
    });
    return count;
  }

  public forget(key: K): void {
    this.hiddenStreaks.delete(key);
  }

  public clear(): void {
    this.hiddenStreaks.clear();
  }
}
